import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { ExpiryListDTO, ExpiryItemDTO, ExpiryStageKey } from '@amrita/shared';

const ORDER: ExpiryStageKey[] = ['expired', 'd3', 'd14', 'd31', 'd38', 'd45', 'd60'];
const STAGE_ACCENT: Record<string, string> = {
  expired: 'border-red-500', d3: 'border-red-400', d14: 'border-orange-400',
  d31: 'border-amber-400', d38: 'border-yellow-400', d45: 'border-lime-400', d60: 'border-tg-secondaryBg',
};

export default function ExpiryPage() {
  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const qc = useQueryClient();
  const [cat, setCat] = useState<string | null>(null);
  const [showWatch, setShowWatch] = useState(false);

  const list = useQuery({
    queryKey: ['expiry'],
    queryFn: () => api.get<ExpiryListDTO>('/api/expiry'),
  });
  const check = useMutation({
    mutationFn: () => api.post('/api/expiry/check', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expiry'] }),
  });

  const items = list.data?.items ?? [];
  const categories = [...new Set(items.map((i) => i.category))].sort();
  const shown = cat ? items.filter((i) => i.category === cat) : items;
  const watch = shown.filter((i) => i.stage === 'watch');

  return (
    <div className="safe-bottom min-h-full">
      <TopBar
        title="Сроки"
        subtitle={list.data ? `${items.length} позиций` : undefined}
        right={isAdmin ? <Button size="sm" variant="secondary" disabled={check.isPending} onClick={() => { if (window.confirm('Разослать уведомления о сроках всем консультантам сейчас?')) check.mutate(); }}>Проверить</Button> : undefined}
      />

      <div className="p-4 space-y-3">
        {list.data?.error && (
          <Card className="text-sm text-tg-hint">
            {list.data.error === 'not_configured' ? 'Источник сроков не настроен (нужна ссылка на CSV в настройках).' : `Источник недоступен: ${list.data.error}`}
          </Card>
        )}

        {list.data && list.data.skipped > 0 && (
          <Card className="text-sm text-tg-hint">
            ⚠️ {list.data.skipped} строк не распознаны — проверьте даты в таблице
          </Card>
        )}

        {/* фильтр по категории */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-1">
            <Chip active={cat === null} onClick={() => setCat(null)}>Все</Chip>
            {categories.map((c) => <Chip key={c} active={cat === c} onClick={() => setCat(c)}>{c}</Chip>)}
          </div>
        )}

        {list.isLoading && <div className="text-tg-hint">Загрузка…</div>}

        {ORDER.map((stage) => {
          const group = shown.filter((i) => i.stage === stage);
          const first = group[0];
          if (!first) return null;
          return (
            <div key={stage} className="space-y-2">
              <div className="text-sm font-semibold text-tg-hint">{first.stageLabel}</div>
              {group.map((i) => <ItemCard key={`${i.category}|${i.name}|${i.expiryDate}`} item={i} />)}
            </div>
          );
        })}

        {/* watch — свёрнуто */}
        {watch.length > 0 && (
          <div className="pt-2">
            <button className="text-sm text-tg-hint underline" onClick={() => setShowWatch((v) => !v)}>
              {showWatch ? 'Скрыть' : `Наблюдение (>60 дней): ${watch.length}`}
            </button>
            {showWatch && <div className="mt-2 space-y-2">{watch.map((i) => <ItemCard key={`${i.category}|${i.name}|${i.expiryDate}`} item={i} />)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={cn('rounded-full px-3 py-1 text-xs', active ? 'bg-brand-orange text-white' : 'bg-tg-secondaryBg text-tg-hint')}>{children}</button>
  );
}

function ItemCard({ item }: { item: ExpiryItemDTO }) {
  return (
    <Card className={cn('border-l-4', STAGE_ACCENT[item.stage] ?? 'border-tg-secondaryBg')}>
      <div className="flex justify-between gap-2">
        <div className="font-medium text-sm">{item.name}</div>
        <div className="text-xs text-tg-hint whitespace-nowrap">{item.daysLeft} дн.</div>
      </div>
      <div className="text-xs text-tg-hint mt-0.5">{item.category}{item.discountPct > 0 && ` · скидка ${item.discountPct}%`}</div>
      {item.actions.length > 0 && (
        <ul className="mt-2 text-xs space-y-0.5 list-disc list-inside">
          {item.actions.map((a, idx) => <li key={idx}>{a}</li>)}
        </ul>
      )}
    </Card>
  );
}
